import React, { FC, useEffect, useRef, useState } from 'react';
import { FieldArray, FormAction, InjectedFormProps } from 'redux-form';
import { NotesField, UnavailableBox } from '@common/components';
import { Spinner } from '@common/components/Spinner';
import { Property } from 'shared-types/Property';
import { SpaPackageType, TimeSlotType } from 'shared-types/SpaPackage';
import { StateProps } from './container';
import { DateTimeSelector } from './DateTimeSelector';
import { GuestCounter } from './GuestCounter';
import {
  GuestDetailsAndSelections,
  validateNamesAreNotDuplicate,
} from './GuestDetailsAndSelections';
import { useQuantity } from './hooks';
import { MessageBoxWrapper, SpaPackageAvailabilityWrapper } from './SpaPackageAvailability.styles';
import { useIsWindowWiderThan } from '@common/hooks';
import { gridTheme } from '@utils/grid';
import { usePageContext } from '@common/components/PageContext';
import { useTranslations } from '@lang';
import { FormattedLink } from '@common/components/FormattedLink';
import { useLabels } from '@common/features/Menu/useLabels';
import { analytics } from '@common/features/analytics';
import {
  useGetSpaPackage,
  useGetSpaSettings,
  useGetSpaPackageTimeslots,
  useGetSpaPackageConfigGroups,
} from '@common/features/spa';
import moment from 'moment';

export type SpaPackageAvailabilityOwnProps = {
  spaPackageId: number;
  property: Property;
  unavailable: boolean;
  setUnavailable: (state: boolean) => void;
  language: string;
  maxGuests?: number;
};

export type SpaPackageAvailabilityFormProps = SpaPackageAvailabilityOwnProps & StateProps;

export const entityHasAvailability = (entity: SpaPackageType): boolean =>
  entity?.configurationGroups
    .filter((group) => group.isExtras === false)
    .every((group) => group.isBookable);

export type SpaPackageAvailabilityCombinedProps = SpaPackageAvailabilityFormProps &
  InjectedFormProps<{}, SpaPackageAvailabilityFormProps> & {
    clearFields: (
      keepTouched: boolean,
      persistentSubmitErrors: boolean,
      ...fields: string[]
    ) => FormAction;
  };

export const SpaPackageAvailability: FC<SpaPackageAvailabilityCombinedProps> = ({
  spaPackageId,
  property,
  clearFields,
  change,
  arrivalTime,
  setUnavailable,
  unavailable,
  selectedDate,
  guests,
  language,
  maxGuests,
}) => {
  const { parentPageName, routingData } = usePageContext();
  const selectedDateString = selectedDate.format('YYYY-MM-DD');

  const [startDate, setStartDate] = useState<string>(selectedDateString);
  const { data: spaPackage, isLoading: isFetchingSpaPackage } = useGetSpaPackage(
    property.id,
    spaPackageId,
    language,
    undefined,
    undefined,
    !unavailable
  );

  const { data: timeslots, isFetching: isFetchingTimeslots } = useGetSpaPackageTimeslots(
    property.id,
    spaPackageId,
    startDate,
    spaPackage?.numberOfGuests,
    7,
    !unavailable
  );

  const { data: spaSettings } = useGetSpaSettings(property.id);
  const spaPhoneNumber = spaSettings?.phoneNumber || '';
  const showGenderNeutral = spaSettings?.showGenderNeutral || false;
  const { hideArrivalTimes } = spaSettings ?? { hideArrivalTimes: false };

  const { t } = useTranslations();
  const labels = useLabels();
  const { supportsTimeslots = false, name = '', id = 0, telephoneOnly = false } = spaPackage || {};

  const timeslotOptions = timeslots?.[selectedDateString];

  const [quantity, setQuantity] = useQuantity(
    Math.min(guests?.length || spaPackage?.numberOfGuests || 1, maxGuests || Infinity),
    (fieldNames) => clearFields(false, false, ...fieldNames)
  );
  const selectedTime = arrivalTime?.startTime;
  const endTime = timeslotOptions?.find(
    (slot: TimeSlotType) => slot.startTime === selectedTime
  )?.endTime;

  const isDesktop = useIsWindowWiderThan(gridTheme.breakpoints.md);

  const {
    data: configurationGroups,
    isFetching: isFetchingConfigGroups,
    error,
  } = useGetSpaPackageConfigGroups(
    spaPackage?.configurationGroups || [],
    property.id,
    id,
    selectedDateString,
    quantity,
    selectedTime,
    endTime
  );

  const loadingDates = (): string[] => {
    if (isFetchingSpaPackage || isFetchingTimeslots) {
      const days: string[] = [];

      for (let index = 0; index < 7; index++) {
        days.push(moment(startDate).add(index, 'days').format('YYYY-MM-DD'));
      }

      return days;
    } else if (isFetchingConfigGroups) {
      return [selectedDateString];
    } else return [];
  };

  const spaPackageWithConfig = { ...spaPackage, timeslots, configurationGroups };

  const noAvailableTimeslots =
    (supportsTimeslots && !timeslotOptions?.length) ||
    !entityHasAvailability(spaPackageWithConfig as SpaPackageType);

  const clearTimeSlots = () => {
    for (let i = 0; i < quantity; i++) {
      clearFields(false, false, `guests[${i}].configurationGroups`);
    }
  };

  const isLoading = isFetchingSpaPackage || isFetchingTimeslots || isFetchingConfigGroups;
  const hasFired = useRef(false);

  if (!isLoading && !loadingDates.length && unavailable && !telephoneOnly && !hasFired.current) {
    analytics.track({
      event: 'denial',
      params: {
        start_date: startDate,
        end_date: '',
        items: [
          {
            item_category: 'spa-packages',
            item_id: `${spaPackage?.id}`,
            item_name: spaPackage?.name,
          },
        ],
      },
    });
    hasFired.current = true;
  }

  useEffect(() => {
    if (isLoading) {
      hasFired.current = false;
    }
    if (!unavailable && (error || noAvailableTimeslots)) {
      setUnavailable(true);
    }
    if (unavailable && !error && !noAvailableTimeslots) {
      setUnavailable(false);
    }
  }, [isLoading, unavailable, error, noAvailableTimeslots, setUnavailable]);

  return (
    <SpaPackageAvailabilityWrapper>
      <Spinner showDelayMessage isActive={isLoading} />
      <GuestCounter
        quantity={quantity}
        setQuantity={setQuantity}
        incrementBy={spaPackageWithConfig?.numberOfGuests || 1}
        maxGuests={maxGuests}
      />
      <DateTimeSelector
        entity={spaPackageWithConfig as SpaPackageType}
        selectedTime={{ label: selectedTime, value: selectedTime }}
        selectedDate={selectedDateString}
        onStartDateChange={setStartDate}
        loadingDates={loadingDates()}
        clearTimeSlots={clearTimeSlots}
        isLoading={isLoading}
        hideArrivalTimes={!!hideArrivalTimes}
        change={change}
      />
      {!telephoneOnly && (
        <FieldArray
          name="guests"
          component={GuestDetailsAndSelections}
          quantity={quantity}
          display={!unavailable}
          entity={spaPackageWithConfig as SpaPackageType}
          change={change}
          validate={validateNamesAreNotDuplicate(t)}
          // @ts-ignore - extra prop passed through FieldArray to component
          showGenderNeutral={showGenderNeutral}
        />
      )}
      {!unavailable && !telephoneOnly && (
        <NotesField
          name="note"
          id="guest_note"
          placeholder={t('booking.additionnalNotesPlaceholder')}
        />
      )}
      {!isLoading && unavailable && !telephoneOnly && (
        <MessageBoxWrapper>
          <UnavailableBox
            title={t('spa.package.unavailableHeading', {
              spaDayCustomLabel: labels?.spadays || 'Spa Day',
            })}
            body={t('spa.package.unavailableDescription', {
              title: name,
              date: selectedDate.format('dddd Do MMMM YYYY'),
            })}
            footer={
              routingData && (
                <FormattedLink format="button" isPrimary={true} href={routingData.href}>
                  {routingData.message}
                </FormattedLink>
              )
            }
          />
        </MessageBoxWrapper>
      )}
      {telephoneOnly && (
        <MessageBoxWrapper>
          <UnavailableBox
            title={t('spa.telephoneOnly', { spaEntity: parentPageName })}
            body={t('spa.callUsOn', {
              phoneNumber: isDesktop ? (
                spaPhoneNumber
              ) : (
                <a href={`tel: ${spaPhoneNumber}`}>{spaPhoneNumber}</a>
              ),
            })}
          />
        </MessageBoxWrapper>
      )}
    </SpaPackageAvailabilityWrapper>
  );
};
